<template>
  <el-card
    shadow="hover"
    class="segment-result-card"
    :class="{ 'is-refining': refining, 'has-cover': clip.coverEnabled }"
  >
    <div class="card-inner">
      <div
        class="thumb"
        :class="{ 'thumb--cover': clip.coverEnabled, 'thumb--disabled': refining }"
        @click="onPlay"
      >
        <el-checkbox
          class="thumb-checkbox"
          :value="selected"
          :disabled="refining"
          @click.native.stop
          @change="onToggleSelect"
        />
        <img
          v-if="clip.thumbnailUrl"
          :src="clip.thumbnailUrl"
          alt=""
          @error="onThumbError"
        />
        <div v-else class="thumb-placeholder">预览</div>
        <el-tag v-if="scoreLabel" class="score-tag" size="mini" type="warning" effect="dark">
          {{ scoreLabel }}
        </el-tag>
        <div v-if="refining" class="thumb-overlay">
          <i class="el-icon-loading" />
          <span>微调处理中…</span>
        </div>
      </div>

      <div class="meta">
        <div class="title">#{{ clip.index }} {{ clip.fileName }}</div>
        <div class="sub">
          {{ formatTime(clip.startMs) }} - {{ formatTime(clip.endMs) }}
          · {{ formatDurationLabel(clip.durationSeconds) }}
        </div>
        <div class="explain">
          <span v-if="peakLine" class="explain-peak">{{ peakLine }}</span>
          <el-tag v-if="reasonLine" size="mini" type="info" effect="plain">{{ reasonLine }}</el-tag>
          <el-tag
            v-if="completenessLine"
            size="mini"
            :type="completenessTagType"
            effect="plain"
          >
            {{ completenessLine }}
          </el-tag>
          <el-tag v-if="clip.userRefined" size="mini" type="success" effect="plain">已微调</el-tag>
        </div>
        <div v-if="feedbackable" class="feedback-row">
          <span class="feedback-label">这段：</span>
          <button
            type="button"
            class="feedback-link"
            :class="{ 'is-picked': feedbackPicked === 'HALF_SONG' }"
            :disabled="refining || feedbackSending"
            @click.stop="sendFeedback('HALF_SONG')"
          >
            {{ feedbackLabels.half }}
          </button>
          <span class="feedback-sep">·</span>
          <button
            type="button"
            class="feedback-link"
            :class="{ 'is-picked': feedbackPicked === 'HAS_CHAT' }"
            :disabled="refining || feedbackSending"
            @click.stop="sendFeedback('HAS_CHAT')"
          >
            {{ feedbackLabels.noise }}
          </button>
          <span class="feedback-sep">·</span>
          <button
            type="button"
            class="feedback-link feedback-link--ok"
            :class="{ 'is-picked': feedbackPicked === 'GOOD' }"
            :disabled="refining || feedbackSending"
            @click.stop="sendFeedback('GOOD')"
          >
            {{ feedbackLabels.good }}
          </button>
        </div>
      </div>

      <div class="actions" :class="{ 'actions--stack': clip.coverEnabled }">
        <el-button
          size="mini"
          class="action-btn"
          :disabled="refining"
          @click="onPlay"
        >
          播放
        </el-button>
        <el-button
          v-if="tuneable"
          size="mini"
          class="action-btn"
          :loading="refining"
          :disabled="refining"
          @click.stop="openTune"
        >
          {{ refining ? '处理中' : '微调' }}
        </el-button>
        <el-button
          size="mini"
          type="primary"
          class="action-btn"
          :disabled="refining"
          @click.stop="onDownload"
        >
          下载
        </el-button>
      </div>
    </div>

    <el-dialog
      title="微调起止时间"
      :visible.sync="tuneVisible"
      width="520px"
      append-to-body
      :close-on-click-modal="!refining"
      :close-on-press-escape="!refining"
      :show-close="!refining"
      @close="onTuneDialogClose"
    >
      <p class="tune-hint">
        开始、结束时间各自独立设置（单位：秒）。应用后按新区间从原片重切，不额外扣次数。
        {{ feedbackCopy.tuneExtra }}
      </p>
      <el-form label-width="88px" size="small" class="tune-form">
        <el-form-item label="开始时间">
          <div class="tune-row">
            <el-input-number
              v-model="tuneStart"
              :min="0"
              :max="Math.max(0, sourceMaxSeconds)"
              :step="1"
              :disabled="refining"
              controls-position="right"
            />
            <span class="tune-unit">秒</span>
            <span class="tune-clock">{{ formatTuneClock(tuneStart) }}</span>
          </div>
        </el-form-item>
        <el-form-item label="结束时间">
          <div class="tune-row">
            <el-input-number
              v-model="tuneEnd"
              :min="0"
              :max="Math.max(0, sourceMaxSeconds)"
              :step="1"
              :disabled="refining"
              controls-position="right"
            />
            <span class="tune-unit">秒</span>
            <span class="tune-clock">{{ formatTuneClock(tuneEnd) }}</span>
          </div>
        </el-form-item>
        <el-form-item label="片段时长">
          <span :class="{ 'tune-invalid': !isTuneValid }">
            {{ tuneDurationSeconds }} 秒（{{ tuneSpanClock }}）
            <template v-if="!isTuneValid">（结束须大于开始，且至少 3 秒）</template>
          </span>
        </el-form-item>
        <el-form-item label="原片范围">
          <span class="tune-range">{{ tuneRangeLine }}</span>
        </el-form-item>
        <el-form-item v-if="tuneClipLabel" label="视频名称">
          <span class="tune-clip-name">{{ tuneClipLabel }}</span>
        </el-form-item>
      </el-form>
      <span slot="footer">
        <el-button :disabled="refining" @click="tuneVisible = false">取消</el-button>
        <el-button type="primary" :loading="refining" :disabled="!isTuneValid || refining" @click="applyTune">
          应用并重切
        </el-button>
      </span>
    </el-dialog>
  </el-card>
</template>

<script>
import { highlightFeedbackCopy } from '../constants/highlightFeedbackCopy';

export default {
  name: 'SegmentResultCard',
  props: {
    clip: { type: Object, required: true },
    selected: { type: Boolean, default: false },
    tuneable: { type: Boolean, default: false },
    feedbackable: { type: Boolean, default: false },
    liveCategory: { type: String, default: 'GAME' },
    sourceDurationSeconds: { type: Number, default: 0 },
    refining: { type: Boolean, default: false },
  },
  data() {
    return {
      tuneVisible: false,
      tuneStart: 0,
      tuneEnd: 30,
      feedbackSending: false,
      feedbackPicked: '',
    };
  },
  watch: {
    'clip.id'() {
      this.feedbackPicked = '';
    },
    refining(val) {
      if (!val && this.tuneVisible) {
        this.tuneVisible = false;
      }
    },
  },
  computed: {
    feedbackCopy() {
      return highlightFeedbackCopy(this.liveCategory);
    },
    feedbackLabels() {
      const copy = this.feedbackCopy;
      return {
        half: copy.feedbackHalf,
        noise: copy.feedbackNoise,
        good: copy.feedbackGood,
      };
    },
    scoreLabel() {
      if (this.clip.score == null || Number.isNaN(Number(this.clip.score))) {
        return '';
      }
      const score = Number(this.clip.score);
      return `热度 ${score.toFixed(2)}`;
    },
    peakLine() {
      if (this.clip.peakSecond == null || Number.isNaN(Number(this.clip.peakSecond))) {
        return '';
      }
      return `峰值 ${this.formatSeconds(this.clip.peakSecond)}`;
    },
    reasonLine() {
      return this.clip.reasonLabel ? String(this.clip.reasonLabel) : '';
    },
    completenessLine() {
      const label = this.clip.completenessLabel;
      if (label === 'FULL_SONG') {
        if (this.liveCategory === 'SING') return '疑似整首';
        if (this.liveCategory === 'DANCE') return '疑似成段';
        return '疑似完整';
      }
      if (label === 'CLIP') {
        return '片段';
      }
      return '';
    },
    completenessTagType() {
      return this.clip.completenessLabel === 'FULL_SONG' ? 'success' : 'warning';
    },
    sourceMaxSeconds() {
      return Math.max(
        3,
        this.sourceDurationSeconds || Math.ceil((this.clip.endMs || 0) / 1000) || 3,
      );
    },
    tuneDurationSeconds() {
      return Math.max(0, Number(this.tuneEnd) - Number(this.tuneStart));
    },
    /** 微调区间在原片上的起止时刻，如 7:26~8:37 */
    tuneSpanClock() {
      return `${this.formatTuneClock(this.tuneStart)}~${this.formatTuneClock(this.tuneEnd)}`;
    },
    /** 原片上的实际截取区间，如 07:26 - 08:37 · 01:11 */
    tuneRangeLine() {
      return `${this.formatSeconds(this.tuneStart)} - ${this.formatSeconds(this.tuneEnd)} · ${this.formatSeconds(this.tuneDurationSeconds)}`;
    },
    /** 当前微调片段文件名，如 #3 xxx_003_07m26s-08m37s.mkv */
    tuneClipLabel() {
      const index = this.clip.index != null ? this.clip.index : '';
      const name = this.clip.fileName || this.clip.name || '';
      if (index === '' && !name) {
        return '';
      }
      return index !== '' ? `#${index} ${name}`.trim() : name;
    },
    isTuneValid() {
      return this.tuneDurationSeconds >= 3
        && this.tuneStart >= 0
        && this.tuneEnd <= this.sourceMaxSeconds
        && this.tuneEnd > this.tuneStart;
    },
  },
  methods: {
    formatTime(ms) {
      return this.formatSeconds(Math.floor((ms || 0) / 1000));
    },
    formatSeconds(totalSeconds) {
      const total = Math.max(0, Math.floor(Number(totalSeconds) || 0));
      const m = Math.floor(total / 60);
      const s = total % 60;
      return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
    },
    /** 微调弹窗：分:秒，分钟不补零，如 0:00、2:20 */
    formatTuneClock(totalSeconds) {
      const total = Math.max(0, Math.floor(Number(totalSeconds) || 0));
      const m = Math.floor(total / 60);
      const s = total % 60;
      return `${m}:${String(s).padStart(2, '0')}`;
    },
    formatDurationLabel(seconds) {
      const total = Math.max(0, Math.floor(Number(seconds) || 0));
      if (total > 60) {
        return this.formatSeconds(total);
      }
      return `${total}s`;
    },
    onToggleSelect(checked) {
      if (this.refining) {
        return;
      }
      this.$emit('toggle-select', this.clip, checked);
    },
    onThumbError() {
      this.$emit('unavailable', this.clip);
    },
    onPlay() {
      if (this.refining) {
        return;
      }
      this.$emit('play', this.clip);
    },
    onDownload() {
      if (this.refining) {
        return;
      }
      this.$emit('download', this.clip);
    },
    openTune() {
      if (this.refining) {
        return;
      }
      this.tuneStart = Math.floor((this.clip.startMs || 0) / 1000);
      this.tuneEnd = Math.ceil((this.clip.endMs || 0) / 1000);
      if (this.tuneEnd <= this.tuneStart) {
        this.tuneEnd = Math.min(this.sourceMaxSeconds, this.tuneStart + 15);
      }
      this.tuneVisible = true;
    },
    onTuneDialogClose() {
      if (this.refining) {
        this.tuneVisible = true;
        return;
      }
      this.tuneVisible = false;
    },
    applyTune() {
      if (!this.isTuneValid || this.refining) {
        if (!this.isTuneValid) {
          this.$message.warning('请检查开始/结束时间（结束须大于开始，且至少 3 秒）');
        }
        return;
      }
      this.$emit('refine', {
        clip: this.clip,
        startMs: this.tuneStart * 1000,
        endMs: this.tuneEnd * 1000,
      });
    },
    sendFeedback(feedbackType) {
      if (this.feedbackSending || this.refining) {
        return;
      }
      this.feedbackSending = true;
      this.feedbackPicked = feedbackType;
      this.$emit('feedback', { clip: this.clip, feedbackType });
      this.$nextTick(() => {
        this.feedbackSending = false;
      });
    },
  },
};
</script>

<style scoped>
.segment-result-card {
  background: #fff;
  height: 100%;
}
.segment-result-card >>> .el-card__body {
  height: 100%;
  padding: 12px;
  box-sizing: border-box;
}
.card-inner {
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 0;
}
.thumb {
  position: relative;
  height: 140px;
  flex: 0 0 auto;
  background: #111827;
  border-radius: 4px;
  overflow: hidden;
  cursor: pointer;
}
.thumb--cover {
  height: auto;
  max-height: 320px;
  aspect-ratio: 9 / 16;
}
.thumb--cover img {
  object-fit: contain;
}
.thumb--disabled {
  cursor: not-allowed;
}
.thumb-checkbox {
  position: absolute;
  top: 8px;
  left: 8px;
  z-index: 2;
  padding: 2px 4px;
  border-radius: 4px;
  background: rgba(255, 255, 255, 0.92);
}
.score-tag {
  position: absolute;
  right: 8px;
  bottom: 8px;
  z-index: 2;
}
.thumb-overlay {
  position: absolute;
  inset: 0;
  z-index: 3;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
  background: rgba(17, 24, 39, 0.72);
  color: #fff;
  font-size: 13px;
}
.thumb img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}
.thumb-placeholder {
  color: #9ca3af;
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
}
.meta {
  flex: 1 1 auto;
  display: flex;
  flex-direction: column;
  margin: 10px 0 8px;
  min-height: 92px;
}
.title {
  font-size: 14px;
  font-weight: 600;
  color: #111827;
  word-break: break-all;
}
.sub {
  font-size: 12px;
  color: #6b7280;
  margin-top: 4px;
}
.explain {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 6px;
  margin-top: 6px;
  min-height: 22px;
  font-size: 12px;
  color: #4b5563;
}
.explain-peak {
  font-variant-numeric: tabular-nums;
}
.feedback-row {
  display: flex;
  align-items: center;
  flex-wrap: nowrap;
  gap: 0;
  margin-top: 8px;
  white-space: nowrap;
  overflow: hidden;
}
.feedback-label {
  font-size: 11px;
  color: #6b7280;
  margin-right: 4px;
  flex: 0 0 auto;
}
.feedback-link {
  border: 0;
  background: transparent;
  padding: 0 2px;
  margin: 0;
  font-size: 11px;
  line-height: 1.2;
  color: #4b5563;
  cursor: pointer;
}
.feedback-link:hover:not(:disabled) {
  color: #2563eb;
}
.feedback-link--ok {
  color: #059669;
}
.feedback-link.is-picked {
  color: #2563eb;
  font-weight: 600;
}
.feedback-link--ok.is-picked {
  color: #047857;
  font-weight: 600;
}
.feedback-link:disabled {
  opacity: 0.45;
  cursor: not-allowed;
}
.feedback-sep {
  font-size: 11px;
  color: #d1d5db;
  margin: 0 2px;
}
.actions {
  display: flex;
  flex-wrap: nowrap;
  align-items: center;
  gap: 8px;
  width: 100%;
  margin-top: auto;
  flex: 0 0 auto;
}
.actions--stack {
  flex-direction: column;
  align-items: stretch;
  gap: 6px;
}
.action-btn {
  margin: 0 !important;
  flex: 1 1 0;
  min-width: 56px;
  height: 28px;
  padding: 0 6px;
  font-size: 12px;
  line-height: 26px;
  overflow: visible;
}
.actions--stack .action-btn {
  flex: none;
  width: 100%;
  min-width: 0;
}
.is-refining {
  opacity: 0.92;
}
.tune-hint {
  margin: 0 0 12px;
  font-size: 12px;
  color: #6b7280;
  line-height: 1.5;
}
.tune-row {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}
.tune-unit {
  color: #374151;
  font-size: 13px;
  font-weight: 600;
}
.tune-clock {
  color: #6b7280;
  font-size: 13px;
  font-variant-numeric: tabular-nums;
}
.tune-form >>> .el-form-item__label {
  text-align: right;
}
.tune-form >>> .el-form-item__content {
  text-align: left;
}
.tune-range {
  color: #374151;
  font-size: 13px;
  font-variant-numeric: tabular-nums;
}
.tune-clip-name {
  color: #374151;
  font-size: 13px;
  line-height: 1.4;
  word-break: break-all;
}
.tune-invalid {
  color: #dc2626;
}
</style>
